export const getFilteredTopics = (topics) => {
    if (!topics || topics.length === 0) {
        return [];
    }

    const firstFiveTopics = topics.slice(0, 5).filter(Boolean);
    const stopWords = new Set(['a', 'an', 'and', 'are', 'how', 'i', 'if', 'in', 'is', 'it', 'of', 'on', 'or', 'the', 'to', 'what']);

    const normalizeWord = (word) => {
        const lowerWord = word.toLowerCase();
        if (lowerWord.length > 3 && lowerWord.endsWith('s') && !lowerWord.endsWith('ss')) {
            return lowerWord.slice(0, -1);
        }
        return lowerWord;
    };

    const levenshteinDistance = (wordA, wordB) => {
        const rows = wordA.length + 1;
        const cols = wordB.length + 1;
        const matrix = Array.from({ length: rows }, () => new Array(cols).fill(0));

        for (let row = 0; row < rows; row += 1) {
            matrix[row][0] = row;
        }
        for (let col = 0; col < cols; col += 1) {
            matrix[0][col] = col;
        }

        for (let row = 1; row < rows; row += 1) {
            for (let col = 1; col < cols; col += 1) {
                const substitutionCost = wordA[row - 1] === wordB[col - 1] ? 0 : 1;

                matrix[row][col] = Math.min(
                    matrix[row - 1][col] + 1,
                    matrix[row][col - 1] + 1,
                    matrix[row - 1][col - 1] + substitutionCost
                );
            }
        }

        return matrix[rows - 1][cols - 1];
    };

    const areWordsSimilar = (wordA, wordB) => {
        if (!wordA || !wordB) {
            return false;
        }

        if (wordA === wordB) {
            return true;
        }

        const minLength = Math.min(wordA.length, wordB.length);
        const maxLength = Math.max(wordA.length, wordB.length);

        if (
            minLength >= 4 &&
            maxLength - minLength <= 3 &&
            (wordA.startsWith(wordB) || wordB.startsWith(wordA))
        ) {
            return true;
        }

        const distance = levenshteinDistance(wordA, wordB);
        if (distance <= 1) {
            return true;
        }

        if (minLength >= 8 && distance <= 2) {
            return true;
        }

        return false;
    };

    const containsSimilarWord = (wordList, targetWord) => {
        for (const word of wordList) {
            if (areWordsSimilar(word, targetWord)) {
                return true;
            }
        }
        return false;
    };

    const topicWords = (topic) => {
        return Array.from(new Set(
            topic
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, ' ')
                .split(/\s+/)
                .map(normalizeWord)
                .filter(Boolean)
        ));
    };

    const nonStopWordCount = (topic) => {
        return topic
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, ' ')
            .split(/\s+/)
            .map(normalizeWord)
            .filter((word) => word && !stopWords.has(word)).length;
    };

    const commonWordCount = (topicA, topicB) => {
        const wordsA = topicWords(topicA);
        const wordsB = topicWords(topicB);
        let count = 0;
        wordsA.forEach((wordA) => {
            if (containsSimilarWord(wordsB, wordA)) count += 1;
        });
        return count;
    };

    const normalizedWords = (topic) => {
        return topic
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, ' ')
            .split(/\s+/)
            .map(normalizeWord)
            .filter(Boolean);
    };

    const contentWords = (topic) => {
        return Array.from(new Set(
            normalizedWords(topic).filter((word) => !stopWords.has(word))
        ));
    };

    const mostlySameTopics = (topicA, topicB) => {
        const wordsA = contentWords(topicA);
        const wordsB = contentWords(topicB);

        if (wordsA.length < 2 || wordsB.length < 2) {
            return false;
        }

        const smaller = wordsA.length <= wordsB.length ? wordsA : wordsB;
        const larger = wordsA.length <= wordsB.length ? wordsB : wordsA;

        let overlap = 0;
        smaller.forEach((word) => {
            if (containsSimilarWord(larger, word)) {
                overlap += 1;
            }
        });

        const overlapRatio = overlap / smaller.length;
        return overlap >= 2 && overlapRatio >= 0.7;
    };

    const removed = new Set();

    for (let i = 0; i < firstFiveTopics.length; i += 1) {
        if (removed.has(i)) continue;
        for (let j = i + 1; j < firstFiveTopics.length; j += 1) {
            if (removed.has(j)) continue;

            const firstTopic = firstFiveTopics[i];
            const secondTopic = firstFiveTopics[j];
            const firstWords = normalizedWords(firstTopic);
            const secondWords = normalizedWords(secondTopic);

            if (firstWords.length === 1 && secondWords.length > 1 && containsSimilarWord(secondWords, firstWords[0])) {
                removed.add(i);
                break;
            }

            if (secondWords.length === 1 && firstWords.length > 1 && containsSimilarWord(firstWords, secondWords[0])) {
                removed.add(j);
                continue;
            }

            if (mostlySameTopics(firstTopic, secondTopic)) {
                const firstContentCount = nonStopWordCount(firstTopic);
                const secondContentCount = nonStopWordCount(secondTopic);

                if (secondContentCount > firstContentCount) {
                    removed.add(i);
                    break;
                }

                if (secondContentCount < firstContentCount) {
                    removed.add(j);
                    continue;
                }

                if (secondTopic.length > firstTopic.length) {
                    removed.add(i);
                    break;
                }

                removed.add(j);
                continue;
            }

            if (commonWordCount(firstTopic, secondTopic) >= 2) {
                const firstContentCount = nonStopWordCount(firstTopic);
                const secondContentCount = nonStopWordCount(secondTopic);

                if (secondContentCount > firstContentCount) {
                    removed.add(i);
                    break;
                }

                if (secondContentCount < firstContentCount) {
                    removed.add(j);
                    continue;
                }

                if (secondTopic.length > firstTopic.length) {
                    removed.add(i);
                    break;
                }

                removed.add(j);
            }
        }
    }

    return firstFiveTopics.filter((_, index) => !removed.has(index));
};
